import React, { useState, useEffect, useCallback } from "react";
import { OrderedSet } from "immutable";
import LabelParser from "../../../utils/LabelParser";
import { IconButton, MenuItem, Select, Slider, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import { FaArrowsAltH, FaArrowsAltV, FaExchangeAlt, FaPencilAlt } from "react-icons/fa";
import utils from "../../../utils/Utils";
import Chord from "../Chord";
import TransposeButtons from "../TransposeButtons";
import { Chart } from "~/shared/types/chart";
import { FaHouse } from "react-icons/fa6";
import { chartEditPath, chartShowPath } from "~/sites/music/routes/paths";
import { TransposeKey } from "~/sites/music/utils/NoteTransposer";

const ColumWidthSliderThumb = (props: React.HTMLAttributes<HTMLSpanElement>) => (
  <span {...props}>
    <FaExchangeAlt className="text-xs" />
  </span>
);

type ChordMeasure = {
  beats: string[];
};

type ChordLine = {
  note: string | null;
  measures: ChordMeasure[];
};

type ChordSection = {
  label: string | null;
  lines: ChordLine[];
};

const scale = { min: 0, max: 100 };

export default function ChartShow({ chart }: { chart: Chart }) {
  const navigate = useNavigate();

  const [position, setPosition] = useState<"horizontal" | "vertical">("horizontal");
  const [numHorizontalChordCharts, setNumHorizontalChordCharts] = useState("1");
  const [transposeKeys, setTransposeKeys] = useState<TransposeKey[]>(['C']);
  const [chordsScale, setChordsScale] = useState(50);
  const [lyricsScale, setLyricsScale] = useState(30);
  const [columnsPosition, setColumnsPosition] = useState(70);

  // Reset each render to accumulate labels in order for section coloring
  let labels = OrderedSet<string | null>([]);

  const getLocalConfig = useCallback(() => {
    const ls = localStorage.getItem("chartsConfig");
    return ls ? JSON.parse(ls) : null;
  }, []);

  // Load persisted state 
  useEffect(() => {
    const ls = getLocalConfig();
    if (ls && ls[chart.id]) {
      const saved = ls[chart.id];
      if (saved.position) setPosition(saved.position);
      if (saved.num_horizontal_chord_charts) setNumHorizontalChordCharts(saved.num_horizontal_chord_charts);
      if (saved.transpose_keys) setTransposeKeys(saved.transpose_keys);
      if (saved.chords_scale != null) setChordsScale(saved.chords_scale);
      if (saved.lyrics_scale != null) setLyricsScale(saved.lyrics_scale);
      if (saved.columns_position != null) setColumnsPosition(saved.columns_position);
    }

  }, []);

  // Re-register keydown handler when chart.id changes so the edit link stays current
  useEffect(() => {
    const keydownHandler = (ev: KeyboardEvent) => {
      if (ev.key === "e" && utils.isMeta(ev)) {
        const edit = window.location.pathname.includes("edit") ? "" : "edit";
        navigate(`/charts/${chart.id}/${edit}`);
      }
      if (ev.key === "m" && utils.isMeta(ev)) navigate("/");
    };

    document.addEventListener("keydown", keydownHandler);
    return () => document.removeEventListener("keydown", keydownHandler);
  }, [chart.id, navigate]);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    const ls = getLocalConfig() || {};
    ls[chart.id] = {
      position,
      num_horizontal_chord_charts: numHorizontalChordCharts,
      transpose_keys: transposeKeys,
      chords_scale: chordsScale,
      lyrics_scale: lyricsScale,
      columns_position: columnsPosition,
    };
    localStorage.setItem("chartsConfig", JSON.stringify(ls));
  }, [position, numHorizontalChordCharts, transposeKeys, chordsScale, lyricsScale, columnsPosition]);

  const editLink = () => {
    const isShow = window.location.pathname.includes("show")
    return isShow ? chartEditPath(chart.id) : chartShowPath(chart.id);
  };

  const chordsColWidth = () => columnsPosition;
  const lyricsColWidth = () => 100 - columnsPosition;

  const fontSize = (scaleKey: "chords_scale" | "lyrics_scale") => {
    const scaleVal = scaleKey === "lyrics_scale" ? lyricsScale : chordsScale;
    const columnWidth = scaleKey === "lyrics_scale" ? lyricsColWidth() : chordsColWidth();
    return (scaleVal * columnWidth) / 1000;
  };

  const sectionColorClass = (label: string | null) => {
    labels = labels.add(label);
    return `section-color-${labels.toArray().indexOf(label)}`;
  };

  const lastSectionLineClass = (section: ChordSection, lineI: number) =>
    section.lines.length - 1 === lineI ? "section-last-line" : "";

  const chordChartCellClass = () => {
    const map: Record<string, string> = {
      1: "w-full",
      2: "w-full md:w-1/2",
      3: "w-full md:w-1/3",
      4: "w-full md:w-1/4",
    };
    return map[numHorizontalChordCharts];
  };

  const lyricText = (text: string | null) =>
    (text || "").split("\n").map((line, i) => <div key={i}>{line}</div>);

  const chordChart = (transposeTo: string) => (
    <div className="chord-chart">
      <div>Key: {transposeTo} </div>
      <table
        className="chart-table w-full"
        style={{ fontSize: `${fontSize("chords_scale")}vw` }}
      >
        <tbody>
          {chart.chords.map((section, sectionI) =>
            section.lines.map((line, lineI) => {
              const lastSectionClass = lastSectionLineClass(section, lineI);
              return (
                <tr
                  key={lineI}
                  className={`chord-section ${sectionColorClass(section.label)}`}
                >
                  {lineI === 0 && (
                    <td
                      rowSpan={section.lines.length}
                      className={`chord-section-label ${lastSectionClass}`}
                    >
                      {section.label}
                    </td>
                  )}
                  {line.measures.map((measure, measureI) => (
                    <td
                      key={measureI}
                      className={`beat-cell ${lastSectionClass}`}
                    >
                      {measure.beats.map((beat, beatI) => (
                        <Chord
                          key={beatI}
                          transpose_to={transposeTo}
                          chart_key={chart.key}
                          beat_text={beat}
                        />
                      ))}
                    </td>
                  ))}
                  <td className="line-note">{line.note}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  if (!chart) return null;

  const author = chart.user;

  return (
    <>
      <div className="chart-container">
        {/* Toolbar row */}
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Tooltip title="Home Page">
              <Link to={'/'}>
                <IconButton size="small" onClick={() => setPosition("horizontal")}>
                  <FaHouse />
                </IconButton>
              </Link>
            </Tooltip>
            {chart.id && (
              <Tooltip title="Edit chart">
                <Link to={editLink()}>
                  <IconButton size="small" id="edit_chart">
                    <FaPencilAlt />
                  </IconButton>
                </Link>
              </Tooltip>
            )}

            {position === "horizontal" ? (
              <Tooltip title="Switch to vertical">
                <IconButton size="small" onClick={() => setPosition("vertical")}>
                  <FaArrowsAltH />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Switch to side-by-side">
                <IconButton size="small" onClick={() => setPosition("horizontal")}>
                  <FaArrowsAltV />
                </IconButton>
              </Tooltip>
            )}

            <Select
              size="small"
              value={numHorizontalChordCharts}
              onChange={(e) => setNumHorizontalChordCharts(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="1">Stack chord charts vertically</MenuItem>
              <MenuItem value="2">2 chord charts per row</MenuItem>
              <MenuItem value="3">3 chord charts per row</MenuItem>
              <MenuItem value="4">4 chord charts per row</MenuItem>
            </Select>

            {author && (
              <div className="flex items-center gap-1 text-sm">
                Chart by:{" "}
                <img className="rounded-full w-6 h-6 inline-block mx-1" src={author.image_url} />
                <a href={`/users/${author.id}/charts`}>{author.name}</a>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <TransposeButtons
              transposeKeys={transposeKeys}
              setTransposeKeys={setTransposeKeys}
              initialKey={chart.key} />
          </div>
        </div>

        {position === "horizontal" && (
          <Slider
            value={columnsPosition}
            onChange={(_, val) => setColumnsPosition(val as number)}
            min={scale.min}
            max={scale.max}
          />
        )}

        <div
          className={["chart-columns-row", position].join(" ")}
          style={{ gridTemplateColumns: `${chordsColWidth()}% ${lyricsColWidth()}%` }}
        >
          <div className="chart-column">
            <div className="chord-charts-container">
              <Slider
                value={chordsScale}
                onChange={(_, val) => setChordsScale(val as number)}
                min={scale.min}
                max={scale.max}
              />

              <h4>{chart.song_title}</h4>

              <div className="flex flex-wrap">
                {transposeKeys.map((transposeTo, key) => (
                  <div key={key} className={chordChartCellClass()}>
                    {chordChart(transposeTo)}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="chart-column">
            <div className="lyrics-container">
              <Slider
                value={lyricsScale}
                onChange={(_, val) => setLyricsScale(val as number)}
                min={scale.min}
                max={scale.max}
              />

              <table
                className="w-full"
                style={{ fontSize: `${fontSize("lyrics_scale")}vw` }}
              >
                <tbody>
                  {chart.lyrics &&
                    chart.lyrics.map((section, sectionI) => {
                      const parser = new LabelParser(section.label); //allows for separate 'display' and 'chord_section' labels
                      const displayLabel = parser.parsed.display_label;
                      const chordSectionLabel = parser.parsed.chord_section_label;
                      return (
                        <tr key={sectionI} className="lyric-section">
                          <td
                            className={`lyric-section-label ${sectionColorClass(chordSectionLabel)} p-2 align-top`}
                          >
                            {displayLabel}
                          </td>
                          <td
                            className={`lyric-section-text ${sectionColorClass(displayLabel)} p-2`}
                          >
                            {lyricText(section.text)}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>

              <div id="notes" className="subtle">
                <h4>Notes:</h4>
                {chart.notes}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
